import {
  Field, TextInput, TextArea, SelectInput, ListInput, MediaField, Panel,
  SectionsEditor, FaqEditor, SeoPanel, slugify, inputCls,
} from "@/components/cms/kit";
import { FormShell, PublishingPanel, STATUS_OPTIONS, useForm } from "@/components/cms/shell";
import { CONTENT_TYPES, PRIMARY_VERTICALS } from "@/lib/taxonomy";
import { AiAssistPanel } from "@/components/cms/AiAssist";
import { ContentQualityPanel } from "@/components/cms/ContentQuality";
import { ImageGenPanel } from "@/components/cms/ImageGen";
import {
  SectionRenderer, ProsCons, VerdictCard, FAQ, ToolLogo, Rating, PricingBadge,
  KeyTakeaways, IntelligenceBox, ComparisonTable, QuickAnswer, LazyImage, ComparisonVisual,
} from "@/components/shared";
import { Plus, Trash2, ExternalLink, Sparkles, AlertTriangle, ClipboardCheck, ChevronDown, ExternalLink as LinkIcon } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/* combo input with suggestions (dropdown + free text) */
function Combo({ value, onChange, options = [], id, ...rest }) {
  const listId = `dl-${id}`;
  return (
    <>
      <input id={id} data-testid={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)} list={listId} className={inputCls} {...rest} />
      <datalist id={listId}>{[...new Set(options)].filter(Boolean).map((o) => <option key={o} value={o} />)}</datalist>
    </>
  );
}

function SlugRow({ f, set, isNew, slugTouched, setSlugTouched, titleKey }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label={titleKey === "name" ? "Name" : "Title"} required>
        <TextInput value={f[titleKey]} onChange={(v) => { set(titleKey, v); if (isNew && !slugTouched) set("slug", slugify(v)); }} testid="cms-title" data-testid="cms-title" />
      </Field>
      <Field label="URL Slug" required hint={isNew ? "Auto-generated from the title; edit if needed." : "Changing this changes the public URL."}>
        <TextInput value={f.slug} onChange={(v) => { set("slug", slugify(v)); setSlugTouched(true); }} testid="cms-slug" data-testid="cms-slug" />
      </Field>
    </div>
  );
}

/* =================================================================== TOOLS */
export function ToolForm({ data, categories, onClose, onSaved }) {
  const isNew = !!data.__new;
  const { f, set, dirty } = useForm({
    ...data, status: data.status || (isNew ? "draft" : "published"),
    best_for: data.best_for || [], features: data.features || [], pros: data.pros || [], cons: data.cons || [],
    body_sections: data.body_sections || [], faqs: data.faqs || [],
  });
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const getPayload = () => {
    const p = { ...data, ...f };
    if (f.domain && !f.website) p.website = `https://${f.domain}`;
    return p;
  };
  const validate = (p) => (!p.slug ? "A slug is required" : !p.name ? "Tool name is required" : null);

  const preview = (
    <article>
      <div className="flex items-center gap-4 mb-4">
        <ToolLogo name={f.name || "Tool"} logo={f.logo} size={56} />
        <div>
          <h1 className="font-display text-3xl font-extrabold text-white">{f.name || "Tool name"}</h1>
          <p className="text-slate-400">{f.tagline}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 mb-6">
        {f.rating != null && <Rating value={Number(f.rating)} />}
        <PricingBadge model={f.pricing_model} />
        {f.website && <a href={f.website} className="text-klyroo-cyan text-sm inline-flex items-center gap-1">Visit <ExternalLink size={12} /></a>}
      </div>
      {f.image && <LazyImage src={f.image} alt={f.name} className="rounded-xl mb-6" />}
      <SectionRenderer sections={(f.body_sections || []).filter((s) => s.heading !== "KLYROO Verdict")} />
      <ProsCons pros={f.pros} cons={f.cons} />
      {f.rating != null && <VerdictCard score={Number(f.rating)} summary={f.verdict?.summary || (f.body_sections || []).find((s) => s.heading === "KLYROO Verdict")?.content} />}
      <FAQ faqs={f.faqs} />
    </article>
  );

  return (
    <FormShell coll="tools" title={isNew ? "New Tool" : `Edit ${data.name}`} isNew={isNew} dirty={dirty} status={f.status}
      getPayload={getPayload} validate={validate} onClose={onClose} onSaved={onSaved} preview={preview}>
      <Panel title="Basic Information">
        <SlugRow f={f} set={set} isNew={isNew} slugTouched={slugTouched} setSlugTouched={setSlugTouched} titleKey="name" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Category"><Combo id="tool-cat" value={f.category} onChange={(v) => set("category", v)} options={categories} placeholder="e.g. AI Assistants" /></Field>
          <Field label="Subcategory"><TextInput value={f.subcategory} onChange={(v) => set("subcategory", v)} /></Field>
        </div>
        <Field label="Short Description / Tagline"><TextArea value={f.tagline} onChange={(v) => { set("tagline", v); set("description", v); }} rows={2} /></Field>
        <Field label="Logo / Icon"><MediaField value={f.logo} onChange={(v) => set("logo", v)} folder="tools" aspect="1 / 1" testid="tool-logo" /></Field>
        <Field label="Cover Image"><MediaField value={f.image} onChange={(v) => set("image", v)} folder="tools" testid="tool-image" /></Field>
      </Panel>

      <Panel title="Content">
        <Field label="Key Features"><ListInput items={f.features} onChange={(v) => set("features", v)} placeholder="Add a feature" testid="tool-features" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Best For"><ListInput items={f.best_for} onChange={(v) => set("best_for", v)} placeholder="Add use case" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Pricing Type"><SelectInput value={f.pricing_model} onChange={(v) => set("pricing_model", v)} options={["Free", "Freemium", "Paid", "Free Trial"]} /></Field>
            <Field label="Starting Price"><TextInput value={f.price} onChange={(v) => set("price", v)} placeholder="e.g. $20/mo" /></Field>
          </div>
        </div>
        <Field label="Rating (0–10)"><TextInput type="number" step="0.1" min="0" max="10" value={f.rating} onChange={(v) => set("rating", v)} /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Pros"><ListInput items={f.pros} onChange={(v) => set("pros", v)} placeholder="Add a pro" /></Field>
          <Field label="Cons"><ListInput items={f.cons} onChange={(v) => set("cons", v)} placeholder="Add a con" /></Field>
        </div>
        <div>
          <span className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Detailed Sections</span>
          <SectionsEditor sections={f.body_sections} onChange={(v) => set("body_sections", v)} folder="tools" />
        </div>
        <div>
          <span className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">FAQs</span>
          <FaqEditor faqs={f.faqs} onChange={(v) => set("faqs", v)} />
        </div>
      </Panel>

      <Panel title="Links & Affiliate" defaultOpen={false}>
        <Field label="Official Website"><TextInput value={f.website} onChange={(v) => set("website", v)} placeholder="https://…" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Documentation"><TextInput value={f.documentation_url} onChange={(v) => set("documentation_url", v)} placeholder="https://…" /></Field>
          <Field label="API"><TextInput value={f.api_url} onChange={(v) => set("api_url", v)} placeholder="https://…" /></Field>
        </div>
        <Field label="Affiliate / Referral Link" hint="Full URL is preserved exactly — tracking & affiliate params are never stripped.">
          <TextInput value={f.affiliate_url} onChange={(v) => set("affiliate_url", v)} placeholder="https://…?ref=klyroo" testid="tool-affiliate" data-testid="tool-affiliate" />
        </Field>
      </Panel>

      <SeoPanel f={f} set={set} folder="tools" />
      <PublishingPanel status={f.status} onChange={(v) => set("status", v)} />
    </FormShell>
  );
}

/* =================================================================== ARTICLES */
function CtaEditor({ items, onChange }) {
  const list = items || [];
  const update = (i, patch) => onChange(list.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  return (
    <div className="space-y-2" data-testid="cta-editor">
      {list.map((c, i) => (
        <div key={i} className="flex gap-2">
          <input value={c.text || ""} onChange={(e) => update(i, { text: e.target.value })} placeholder="Button text" className={inputCls} />
          <input value={c.url || ""} onChange={(e) => update(i, { url: e.target.value })} placeholder="https://… (affiliate ok)" className={inputCls} />
          <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} className="p-2 text-slate-500 hover:text-klyroo-red"><Trash2 size={15} /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...list, { text: "", url: "" }])} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 text-white text-sm hover:bg-white/10"><Plus size={14} /> Add CTA Button</button>
    </div>
  );
}

const CHECKLIST_ITEMS = [
  "Facts verified", "Source reviewed", "Claims requiring verification checked",
  "Title reviewed", "Body reviewed", "SEO title reviewed", "Meta description reviewed",
  "Tags reviewed", "FAQs reviewed", "Images reviewed",
];

function ResearchContextPanel({ f, set, onClose, onSaved }) {
  const [ctx, setCtx] = useState(null);
  const [openClaims, setOpenClaims] = useState(false);
  const [checks, setChecks] = useState(() => f.review_checklist || {});
  const [confirming, setConfirming] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const isAi = !!f.ai_generated;
  const isDraft = (f.status || "draft") !== "published";
  const toggleCheck = (c, v) => setChecks((p) => { const next = { ...p, [c]: v }; set && set("review_checklist", next); return next; });

  useEffect(() => {
    if (f.research_item_id) api.researchArticleContext(f.research_item_id).then((d) => setCtx(d.context)).catch(() => setCtx(null));
  }, [f.research_item_id]);

  if (!isAi && !f.research_item_id) return null;
  const claims = ctx?.claims_requiring_verification || [];
  const doneCount = CHECKLIST_ITEMS.filter((c) => checks[c]).length;
  const allDone = doneCount === CHECKLIST_ITEMS.length;

  const publish = async () => {
    if (!f.slug) { toast.error("Save the draft first"); return; }
    setPublishing(true);
    try {
      await api.articlePublish(f.slug, { checklist: checks });
      toast.success("Article published");
      setConfirming(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
    finally { setPublishing(false); }
  };

  return (
    <div className="rounded-xl border border-purple-400/25 bg-purple-400/5 p-4 mb-4" data-testid="research-context-panel">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {isAi && <span className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded bg-purple-400/20 text-purple-200" data-testid="ai-generated-indicator"><Sparkles size={11} /> AI Generated</span>}
        {isAi && f.needs_review !== false && <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-400/15 text-amber-300" data-testid="needs-review-indicator">Human Review Required</span>}
        <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${isDraft ? "bg-white/10 text-slate-300" : "bg-emerald-400/15 text-emerald-300"}`} data-testid="article-status-indicator">{isDraft ? "Draft" : "Published"}</span>
      </div>

      {claims.length > 0 && (
        <div className="rounded-lg bg-klyroo-red/10 border border-klyroo-red/30 px-3 py-2 mb-3" data-testid="claims-warning">
          <button onClick={() => setOpenClaims((v) => !v)} className="flex items-center gap-1.5 text-klyroo-red text-xs font-mono w-full"><AlertTriangle size={13} /> {claims.length} claim{claims.length === 1 ? "" : "s"} require verification <ChevronDown size={13} className={`ml-auto transition-transform ${openClaims ? "rotate-180" : ""}`} /></button>
          {openClaims && <ul className="list-disc pl-5 mt-2 space-y-0.5" data-testid="claims-list">{claims.map((c, i) => <li key={i} className="text-xs text-amber-200">{c}</li>)}</ul>}
        </div>
      )}

      {ctx && (
        <details className="mb-3" data-testid="research-context-details">
          <summary className="text-[10px] font-mono uppercase tracking-widest text-purple-300 cursor-pointer">Research Context</summary>
          <div className="mt-2 space-y-1.5 text-xs text-slate-300">
            {ctx.original_story && <div><span className="text-slate-500">Original story:</span> {ctx.original_story}</div>}
            {ctx.source && <div><span className="text-slate-500">Primary source:</span> {ctx.source} {ctx.publisher_type ? `(${ctx.publisher_type})` : ""} {ctx.source_url && <a href={ctx.source_url} target="_blank" rel="noreferrer" className="text-klyroo-cyan hover:underline inline-flex items-center gap-0.5"><LinkIcon size={10} /> link</a>}</div>}
            {ctx.summary && <div><span className="text-slate-500">Brief summary:</span> {ctx.summary}</div>}
            {ctx.recommended_angle && <div><span className="text-slate-500">Recommended angle:</span> {ctx.recommended_angle}</div>}
            {(ctx.key_facts || []).length > 0 && <div><span className="text-slate-500">Key facts:</span><ul className="list-disc pl-5">{ctx.key_facts.map((k, i) => <li key={i}>{k}</li>)}</ul></div>}
            {ctx.confidence != null && <div><span className="text-slate-500">Research confidence:</span> {Math.round((ctx.confidence || 0) * 100)}%</div>}
          </div>
        </details>
      )}

      <details data-testid="review-checklist-wrap">
        <summary className="text-[10px] font-mono uppercase tracking-widest text-purple-300 cursor-pointer inline-flex items-center gap-1"><ClipboardCheck size={12} /> Review checklist ({doneCount}/{CHECKLIST_ITEMS.length})</summary>
        <div className="mt-2 grid sm:grid-cols-2 gap-1" data-testid="review-checklist">
          {CHECKLIST_ITEMS.map((c) => (
            <label key={c} className="flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={!!checks[c]} onChange={(e) => toggleCheck(c, e.target.checked)} data-testid={`checklist-${c.replace(/\s+/g, "-").toLowerCase()}`} className="w-3.5 h-3.5 accent-klyroo-cyan" /> {c}</label>
          ))}
        </div>
      </details>

      {isDraft && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10">
          <button onClick={() => setConfirming(true)} data-testid="article-publish-btn" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-emerald-400/20 text-emerald-200 text-sm font-semibold hover:bg-emerald-400/30">Publish…</button>
          <span className="text-[10px] font-mono text-slate-500">Draft stays private until you publish</span>
        </div>
      )}

      {confirming && (
        <div className="fixed inset-0 z-[160] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !publishing && setConfirming(false)}>
          <div className="w-full max-w-md card-surface rounded-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="publish-confirm">
            <h3 className="font-display font-bold text-white text-lg mb-3">Publish this article?</h3>
            <div className="text-sm text-slate-300 space-y-1 mb-3">
              <div><span className="text-slate-500">Title:</span> {f.title || "(untitled)"}</div>
              <div><span className="text-slate-500">Category:</span> {f.category || "—"}</div>
              <div><span className="text-slate-500">AI-generated:</span> {isAi ? "Yes" : "No"} · <span className="text-slate-500">Needs review:</span> {f.needs_review !== false && isAi ? "Yes" : "No"}</div>
              <div><span className="text-slate-500">Checklist:</span> {doneCount}/{CHECKLIST_ITEMS.length} complete</div>
              <div className="text-emerald-300 font-mono text-xs">Status change: Draft → Published</div>
            </div>
            {isAi && !allDone && <div className="rounded-lg bg-amber-400/10 border border-amber-400/30 px-3 py-2 text-amber-200 text-xs mb-3" data-testid="publish-checklist-warning">⚠ Some review checklist items are incomplete. You can still publish — the decision is yours.</div>}
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirming(false)} disabled={publishing} data-testid="publish-cancel" className="px-4 py-2 rounded-lg text-slate-300 hover:bg-white/5 text-sm disabled:opacity-50">Cancel</button>
              <button onClick={publish} disabled={publishing} data-testid="publish-confirm-btn" className="px-5 py-2 rounded-lg bg-emerald-400/25 text-emerald-100 font-semibold text-sm hover:bg-emerald-400/40 disabled:opacity-50">{publishing ? "Publishing…" : "Publish"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function ArticleForm({ data, categories, onClose, onSaved }) {
  const isNew = !!data.__new;
  const { f, set, dirty } = useForm({
    ...data, status: data.status || (isNew ? "draft" : "published"),
    tags: data.tags || [], key_takeaways: data.key_takeaways || [], body_sections: data.body_sections || [],
    faqs: data.faqs || [], cta_buttons: data.cta_buttons || [], intelligence: data.intelligence || {},
  });
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const imageContext = useMemo(
    () => ({ article_slug: f.slug, research_item_id: f.research_item_id, title: f.title, category: f.category, summary: f.subtitle }),
    [f.slug, f.research_item_id, f.title, f.category, f.subtitle]
  );
  const getPayload = () => ({ ...data, ...f });
  const validate = (p) => (!p.slug ? "A slug is required" : !p.title ? "Article title is required" : null);

  const preview = (
    <article>
      {f.image && <LazyImage src={f.image} alt={f.title} className="rounded-xl mb-6" />}
      <div className="text-klyroo-cyan text-xs font-mono uppercase tracking-widest mb-2">{f.category} {f.content_type ? `· ${f.content_type}` : ""}</div>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white mb-2">{f.title || "Article title"}</h1>
      <p className="text-lg text-slate-400 mb-4">{f.subtitle}</p>
      <div className="text-sm text-slate-500 mb-6">By {f.author || "KLYROO Staff"}</div>
      <KeyTakeaways items={f.key_takeaways} />
      <IntelligenceBox intel={f.intelligence} />
      <SectionRenderer sections={f.body_sections} />
      {(f.cta_buttons || []).length > 0 && (
        <div className="flex flex-wrap gap-3 my-6">
          {f.cta_buttons.map((c, i) => <a key={i} href={c.url} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-klyroo-cyan text-black font-semibold text-sm">{c.text || "Learn more"} <ExternalLink size={14} /></a>)}
        </div>
      )}
      <FAQ faqs={f.faqs} />
    </article>
  );

  return (
    <FormShell coll="articles" title={isNew ? "New Article" : `Edit ${data.title}`} isNew={isNew} dirty={dirty} status={f.status}
      getPayload={getPayload} validate={validate} onClose={onClose} onSaved={onSaved} preview={preview}>
      <AiAssistPanel f={f} set={set} />
      <ResearchContextPanel f={f} set={set} onClose={onClose} onSaved={onSaved} />
      <ContentQualityPanel f={f} />
      {f.slug ? (
        <ImageGenPanel
          context={imageContext}
          onUseImage={(url) => { set("image", url); set("thumbnail", url); }}
        />
      ) : (
        <div className="rounded-xl border border-white/10 bg-klyroo-bg/40 p-4 mb-4 text-xs text-slate-400" data-testid="image-gen-locked">Save the article once to enable AI image generation.</div>
      )}
      <Panel title="Basic Information">
        <SlugRow f={f} set={set} isNew={isNew} slugTouched={slugTouched} setSlugTouched={setSlugTouched} titleKey="title" />
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Category"><Combo id="art-cat" value={f.category} onChange={(v) => set("category", v)} options={[...new Set([...PRIMARY_VERTICALS, ...categories])]} placeholder="AI, Tech, Gaming…" /></Field>
          <Field label="Content Type"><SelectInput id="art-type" data-testid="art-type" value={f.content_type} onChange={(v) => set("content_type", v)} options={[...CONTENT_TYPES, ...(f.content_type && !CONTENT_TYPES.includes(f.content_type) ? [f.content_type] : [])]} /></Field>
          <Field label="Author"><TextInput value={f.author} onChange={(v) => set("author", v)} placeholder="KLYROO Staff" /></Field>
        </div>
        <Field label="Excerpt / Subtitle"><TextArea value={f.subtitle} onChange={(v) => set("subtitle", v)} rows={2} data-testid="article-subtitle" /></Field>
        <Field label="Tags"><ListInput items={f.tags} onChange={(v) => set("tags", v)} placeholder="Add a tag" testid="article-tags" /></Field>
        <Field label="Featured Image"><MediaField value={f.image} onChange={(v) => set("image", v)} folder="articles" testid="article-image" /></Field>
      </Panel>

      <Panel title="Article Content">
        <Field label="Key Takeaways"><ListInput items={f.key_takeaways} onChange={(v) => set("key_takeaways", v)} placeholder="Add a takeaway" /></Field>
        <div>
          <span className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Body</span>
          <SectionsEditor sections={f.body_sections} onChange={(v) => set("body_sections", v)} folder="articles" />
        </div>
        <div>
          <span className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">FAQs</span>
          <FaqEditor faqs={f.faqs} onChange={(v) => set("faqs", v)} />
        </div>
      </Panel>

      <Panel title="Monetization / CTA Buttons" defaultOpen={false}>
        <p className="text-xs text-slate-500 -mt-1">Affiliate links can also be inserted inline in the body via the link tool. Full URLs are preserved.</p>
        <CtaEditor items={f.cta_buttons} onChange={(v) => set("cta_buttons", v)} />
      </Panel>

      <SeoPanel f={f} set={set} folder="articles" />
      <PublishingPanel status={f.status} onChange={(v) => set("status", v)} />
    </FormShell>
  );
}

/* =================================================================== COMPARISONS */
function ProductsEditor({ products, onChange }) {
  const list = products || [];
  const update = (i, patch) => onChange(list.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  return (
    <div className="space-y-3" data-testid="products-editor">
      {list.map((p, i) => (
        <div key={i} className="rounded-xl border border-white/10 bg-klyroo-bg/40 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono uppercase tracking-widest text-klyroo-cyan">Product {i + 1}</span>
            {list.length > 2 && <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} className="p-1 text-slate-500 hover:text-klyroo-red"><Trash2 size={14} /></button>}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Product Name" required><TextInput value={p.name} onChange={(v) => update(i, { name: v })} testid={`product-name-${i}`} data-testid={`product-name-${i}`} /></Field>
            <Field label="Brand"><TextInput value={p.brand} onChange={(v) => update(i, { brand: v })} /></Field>
          </div>
          <Field label="Short Description"><TextArea value={p.description} onChange={(v) => update(i, { description: v })} rows={2} /></Field>
          <Field label="Image"><MediaField value={p.image} onChange={(v) => update(i, { image: v })} folder="comparisons" aspect="1 / 1" testid={`product-image-${i}`} /></Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Official / Product URL"><TextInput value={p.url} onChange={(v) => update(i, { url: v })} placeholder="https://…" /></Field>
            <Field label="Affiliate Link" hint="Preserved exactly."><TextInput value={p.affiliate_url} onChange={(v) => update(i, { affiliate_url: v })} placeholder="https://…?ref=klyroo" /></Field>
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...list, { name: "", brand: "", image: "", url: "", affiliate_url: "", description: "" }])} data-testid="product-add"
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 text-white text-sm hover:bg-white/10"><Plus size={14} /> Add Product</button>
    </div>
  );
}

function TableBuilder({ rows, productNames, onChange }) {
  const list = rows || [];
  const n = productNames.length;
  const update = (i, patch) => onChange(list.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const setVal = (i, k, val) => {
    const values = [...(list[i].values || [])];
    while (values.length < n) values.push("");
    values[k] = val;
    update(i, { values });
  };
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= list.length) return;
    const c = [...list]; [c[i], c[j]] = [c[j], c[i]]; onChange(c);
  };
  return (
    <div className="space-y-2" data-testid="table-builder">
      <div className="hidden sm:grid gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-500" style={{ gridTemplateColumns: `1.2fr repeat(${n}, 1fr) auto` }}>
        <span>Feature</span>
        {productNames.map((pn, k) => <span key={k} className="truncate">{pn || `Product ${k + 1}`}</span>)}
        <span></span>
      </div>
      {list.map((r, i) => (
        <div key={i} className="grid gap-2 items-center" style={{ gridTemplateColumns: `1.2fr repeat(${n}, 1fr) auto` }}>
          <input value={r.feature || ""} onChange={(e) => update(i, { feature: e.target.value })} placeholder="Feature" className={inputCls} data-testid={`row-feature-${i}`} />
          {Array.from({ length: n }).map((_, k) => (
            <input key={k} value={(r.values || [])[k] || ""} onChange={(e) => setVal(i, k, e.target.value)} placeholder="—" className={inputCls} data-testid={`row-val-${i}-${k}`} />
          ))}
          <div className="flex">
            <button type="button" onClick={() => move(i, -1)} className="p-1 text-slate-500 hover:text-white text-xs">↑</button>
            <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} className="p-1 text-slate-500 hover:text-klyroo-red"><Trash2 size={13} /></button>
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...list, { feature: "", values: Array(n).fill("") }])} data-testid="row-add"
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 text-white text-sm hover:bg-white/10"><Plus size={14} /> Add Row</button>
    </div>
  );
}

function KeyValueEditor({ obj, onChange, keyPlaceholder = "Label", valPlaceholder = "Value", testid = "kv-editor" }) {
  const entries = Object.entries(obj || {});
  const setEntries = (arr) => onChange(Object.fromEntries(arr));
  return (
    <div className="space-y-2" data-testid={testid}>
      {entries.map(([k, v], i) => (
        <div key={i} className="flex gap-2">
          <input value={k} onChange={(e) => { const a = [...entries]; a[i] = [e.target.value, v]; setEntries(a); }} placeholder={keyPlaceholder} className={inputCls} data-testid={`${testid}-key-${i}`} />
          <input value={v} onChange={(e) => { const a = [...entries]; a[i] = [k, e.target.value]; setEntries(a); }} placeholder={valPlaceholder} className={inputCls} data-testid={`${testid}-val-${i}`} />
          <button type="button" onClick={() => setEntries(entries.filter((_, j) => j !== i))} data-testid={`${testid}-del-${i}`} className="p-2 text-slate-500 hover:text-klyroo-red"><Trash2 size={15} /></button>
        </div>
      ))}
      <button type="button" onClick={() => setEntries([...entries, ["", ""]])} data-testid={`${testid}-add`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 text-white text-sm hover:bg-white/10"><Plus size={14} /> Add</button>
    </div>
  );
}

export function ComparisonForm({ data, categories, onClose, onSaved }) {
  const isNew = !!data.__new;
  // Migrate existing comparison (items/columns/logos) into a products array for the visual builder.
  const initialProducts = (() => {
    if (data.products?.length) return data.products;
    const names = data.items || [];
    return names.map((nm, i) => ({ name: nm, brand: "", image: (data.logos || [])[i] || "", url: "", affiliate_url: "", description: "" }));
  })();
  const { f, set, dirty } = useForm({
    ...data, status: data.status || (isNew ? "draft" : "published"),
    products: initialProducts.length ? initialProducts : [{ name: "", brand: "", image: "", url: "", affiliate_url: "", description: "" }, { name: "", brand: "", image: "", url: "", affiliate_url: "", description: "" }],
    rows: data.rows || [], quick_answer: data.quick_answer || {}, body_sections: data.body_sections || [], faqs: data.faqs || [],
  });
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const productNames = (f.products || []).map((p) => p.name);

  const getPayload = () => {
    const kept = (f.products || []).filter((pr) => pr.name);
    const names = kept.map((pr) => pr.name);
    const p = {
      ...data, ...f,
      items: names,
      columns: ["Feature", ...names],
      logos: kept.map((pr) => pr.image || ""), // index-aligned with kept products
      h1: f.title || data.h1,
    };
    return p;
  };
  const validate = (p) => {
    if (!p.slug) return "A slug is required";
    if (!p.title) return "Comparison title is required";
    if ((p.items || []).length < 2) return "Add at least 2 products";
    return null;
  };

  const preview = (
    <article>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white mb-4">{f.title || "Comparison title"}</h1>
      <ComparisonVisual c={{ ...f, items: productNames, slug: f.slug || "preview" }} detail />
      <div className="mt-6"><QuickAnswer best={f.quick_answer} /></div>
      <div className="mt-6"><ComparisonTable columns={["Feature", ...productNames]} rows={f.rows} /></div>
      <div className="mt-6"><SectionRenderer sections={f.body_sections} /></div>
      <FAQ faqs={f.faqs} />
    </article>
  );

  return (
    <FormShell coll="comparisons" title={isNew ? "New Comparison" : `Edit ${data.title}`} isNew={isNew} dirty={dirty} status={f.status}
      getPayload={getPayload} validate={validate} onClose={onClose} onSaved={onSaved} preview={preview}>
      <Panel title="Basic Information">
        <SlugRow f={f} set={set} isNew={isNew} slugTouched={slugTouched} setSlugTouched={setSlugTouched} titleKey="title" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Category"><Combo id="cmp-cat" value={f.category} onChange={(v) => set("category", v)} options={categories} placeholder="AI Models, Hardware…" /></Field>
          <Field label="Featured Image"><MediaField value={f.image} onChange={(v) => set("image", v)} folder="comparisons" testid="cmp-image" /></Field>
        </div>
      </Panel>

      <Panel title="Products">
        <ProductsEditor products={f.products} onChange={(v) => set("products", v)} />
      </Panel>

      <Panel title="Comparison Table">
        <p className="text-xs text-slate-500 -mt-1">One row per feature. Columns update automatically from your products.</p>
        <TableBuilder rows={f.rows} productNames={productNames} onChange={(v) => set("rows", v)} />
      </Panel>

      <Panel title="Quick Answer / Verdict" defaultOpen={false}>
        <p className="text-xs text-slate-500 -mt-1">Short "best for X" recommendations shown near the top.</p>
        <KeyValueEditor obj={f.quick_answer} onChange={(v) => set("quick_answer", v)} keyPlaceholder="e.g. Best overall" valPlaceholder="e.g. ChatGPT" testid="quick-answer-editor" />
      </Panel>

      <Panel title="Detailed Content" defaultOpen={false}>
        <SectionsEditor sections={f.body_sections} onChange={(v) => set("body_sections", v)} folder="comparisons" />
        <div>
          <span className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">FAQs</span>
          <FaqEditor faqs={f.faqs} onChange={(v) => set("faqs", v)} />
        </div>
      </Panel>

      <SeoPanel f={f} set={set} folder="comparisons" />
      <PublishingPanel status={f.status} onChange={(v) => set("status", v)} />
    </FormShell>
  );
}
